import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '../../components/Typography';
import { useTranslation } from 'react-i18next';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button/index';
import openedEye from '../../assets/openedEye.svg';
import line from '../../assets/line.png';
import closeIcon from '../../assets/closeIcon.png';
import desktopImg from '../../assets/desktopImg.png';
import desktop from '../../assets/desktop.svg';
import closedEye from '../../assets/closedEye.svg';
import person from '../../assets/person.svg';
import group from '../../assets/group.svg';
import { CheckBox } from '../../components/CheckBox';
import { setAccessToken, setRefreshToken } from '../../utilities/storage/tokenStorage';
import type { AuthTokens } from '../../models/auth';
import { validateEmail } from '../../utilities/utils';
import { checkEmail } from '../../stores/User';
import api from '../../utilities/api';
import {
    formatRateLimitedMessage,
    getRateLimitRetryAfter,
    isRateLimitedError
} from '../../utilities/rateLimitError';
import './register.css';

export function Register(): JSX.Element {
    const history = useHistory();
    const { t } = useTranslation();
    const { code } = useParams<{ code: string }>();
    const [error, setError] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [checked, setCheckedValue] = useState<boolean>(false);
    const [values, setValues] = useState({
        familyCode: code ? code : '',
        email: '',
        password: '',
        showPassword: false
    });

    useEffect(() => {
        setValues((prev) => ({ ...prev, familyCode: code ? code : '' }));
    }, [code]);

    useEffect(() => {
        setError(false);
    }, [values.email, values.password, values.familyCode]);

    const onRegister = async (): Promise<void> => {
        setError(false);
        if (!values.email || !values.password || !values.familyCode || !checked) return;

        try {
            const { data } = await api.post<AuthTokens>('/auth/register', {
                email: values.email,
                password: values.password,
                codeBe: values.familyCode
            });
            await setAccessToken(data.accessToken);
            await setRefreshToken(data.refreshToken);
            history.push('/home');
        } catch (err: any) {
            setError(true);
            const errCode = err?.response?.data?.code;
            if (isRateLimitedError(err)) {
                setErrorText(formatRateLimitedMessage(getRateLimitRetryAfter(err)));
            } else if (errCode === 'EMAIL_TAKEN') {
                setErrorText(
                    t(
                        'Un compte avec cette adresse e-mail existe déjà, veuillez utiliser une autre adresse e-mail'
                    ) ?? ''
                );
            } else if (errCode === 'INVALID_CODE') {
                setErrorText(t('Le code Bonne Etoile est invalide') ?? '');
            } else {
                setErrorText(
                    err?.response?.data?.error || (t("Erreur lors de l'inscription") ?? '')
                );
            }
        }
    };

    const handleSubmit = async (): Promise<void> => {
        if (!validateEmail(values.email)) {
            setError(true);
            setErrorText(
                t(
                    "Cette adresse e-mail n'existe pas, veuillez utiliser une autre adresse e-mail"
                ) ?? ''
            );
            return;
        }
        if (values.password.length < 6) {
            setError(true);
            setErrorText(t('Mot de passe invalide ou trop court, veuillez réessayer svp') ?? '');
            return;
        }

        const emailExists = await checkEmail(values.email);
        if (emailExists) {
            setError(true);
            setErrorText(
                t(
                    'Un compte avec cette adresse e-mail existe déjà, veuillez utiliser une autre adresse e-mail'
                ) ?? ''
            );
            return;
        }

        try {
            // POST : le code famille est un porteur de droit, il ne doit pas transiter par
            // l'URL (logs d'accès, en-tête Referer, historique du navigateur).
            const { data: isValidCode } = await api.post('/family/validate-codebe', {
                codeBe: values.familyCode
            });
            if (!isValidCode) {
                setError(true);
                setErrorText(t('Le code Bonne Etoile est invalide') ?? '');
                return;
            }
            await onRegister();
        } catch (err) {
            setError(true);
            // Sans ce cas, la limite de débit s'afficherait en « code invalide » — trompeur
            // pour quelqu'un dont le code est bon.
            if (isRateLimitedError(err)) {
                setErrorText(formatRateLimitedMessage(getRateLimitRetryAfter(err)));
                return;
            }
            setErrorText(t('Le code Bonne Etoile est invalide') ?? '');
        }
    };

    const isFormValid =
        checked && values.familyCode !== '' && values.email !== '' && values.password !== '';

    return (
        <div className="register-page">
            <div className="register-sidebar" style={{ backgroundImage: `url(${desktop})` }}>
                <img src={desktopImg} alt="desktop" />
            </div>
            <div className="register-content">
                <img
                    className="register-close"
                    src={closeIcon}
                    alt="close"
                    onClick={() => history.push('/login/me')}
                />
                <Typography
                    variant="h1"
                    sx={{
                        fontWeight: '700',
                        paddingTop: '2rem',
                        paddingBottom: '1rem',
                        textAlign: 'left',
                        width: '100%'
                    }}>
                    {t('Rejoindre un espace privé')}
                </Typography>
                <Typography variant="p" className="register-description">
                    {t(
                        'Saisissez votre code "Bonne étoile" ainsi que les informations de connexion de votre choix pour activer votre accès personnel et rejoindre l\u2019espace Stimul\u2019in de votre / vos proche(s).'
                    )}
                </Typography>
                <form
                    className="register-form"
                    onSubmit={(e) => e.preventDefault()}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            event.stopPropagation();
                            handleSubmit();
                        }
                    }}>
                    <Input
                        sxRoot={{ paddingBottom: '0.5rem', width: '100%' }}
                        label={t('Mon Code "Bonne étoile"') ?? ''}
                        placeholder="ex: 3UdS5d"
                        value={values.familyCode}
                        onChange={(value: string) =>
                            setValues((prev) => ({
                                ...prev,
                                familyCode: value?.trim() ?? ''
                            }))
                        }
                        imgIntoInput={group}
                    />
                    <Input
                        sxRoot={{ paddingBottom: '0.5rem', width: '100%' }}
                        label={t('Mon email') ?? ''}
                        placeholder={t('ex : jean.dupont@hotmail.com') ?? ''}
                        value={values.email}
                        onChange={(value: string) =>
                            setValues((prev) => ({
                                ...prev,
                                email: value?.trim() ?? ''
                            }))
                        }
                        imgIntoInput={person}
                    />
                    <Input
                        sxRoot={{ paddingBottom: '0.5rem', width: '100%' }}
                        label={t('Mon mot de passe') ?? ''}
                        placeholder="********"
                        type={values.showPassword ? '' : 'password'}
                        value={values.password}
                        onChange={(value: string) =>
                            setValues((prev) => ({
                                ...prev,
                                password: value?.trim() ?? ''
                            }))
                        }
                        imgIntoInput={values.showPassword ? openedEye : closedEye}
                        btnAriaLabel={
                            (values.showPassword
                                ? t('Masquer le mot de passe')
                                : t('Afficher le mot de passe')) ?? ''
                        }
                        btnClick={() =>
                            setValues((prev) => ({
                                ...prev,
                                showPassword: !prev.showPassword
                            }))
                        }
                    />
                    <div className="register-policies">
                        <CheckBox
                            sx={{ cursor: 'pointer', alignSelf: 'center', marginRight: '0.5rem' }}
                            checked={checked}
                            setChecked={() => setCheckedValue(!checked)}
                        />
                        <Typography sx={{ width: 'auto', textAlign: 'start' }}>
                            <span>{t('En vous connectant, vous acceptez les ')}</span>
                            <a
                                href="https://appv1.stimulin.fr/cgu.html"
                                target="_blank"
                                rel="noreferrer">
                                {t("conditions générales d'utilisation")}
                            </a>
                            <span>{t('et')}</span>
                            <a
                                href="https://appv1.stimulin.fr/politique-de-confidentialite.html"
                                target="_blank"
                                rel="noreferrer">
                                {t('politique de confidentialité')}
                            </a>
                            <span>{t(" de Stimul'in.")}</span>
                        </Typography>
                    </div>
                </form>

                {error && (
                    <Typography variant="p" sx={{ color: 'var(--color-error)' }}>
                        {errorText}
                    </Typography>
                )}

                <div className="register-cta">
                    <Button
                        label={t('Activer') ?? ''}
                        theme="PRIMARY"
                        size="MEDIUM"
                        fullWidth
                        disabled={!isFormValid}
                        action={handleSubmit}
                    />
                </div>

                <div className="register-separator">
                    <img src={line} alt="line" />
                    <span>{t('Vous avez déjà un compte ?')}</span>
                    <img src={line} alt="line" />
                </div>

                <div className="register-login-cta">
                    <Button
                        label={t('Me connecter') ?? ''}
                        theme="SECONDARY"
                        size="MEDIUM"
                        fullWidth
                        action={() => history.push('/login/me')}
                    />
                </div>
            </div>
        </div>
    );
}
